import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The room's side panels: one rail of tabs down the right edge, each opening a
 * panel that is either TEMPORARY (slides out over the page; Escape or × puts it
 * away) or PINNED (docked in the one slot beside the page, where the chat used
 * to live). One slot, so pinning a panel unpins whichever was there.
 *
 * Narrow screens have no room for a slot: there every panel slides out.
 */

export type PanelKey = 'feed' | 'chat' | 'brief' | 'runs' | 'tags' | 'shared' | 'leads' | 'notes';

/**
 * THREE TABS, NOT SEVEN. The rail had a tab per panel: the chat, the state of
 * the room, tags, leads, shared, runs and the feed. Seven words down the edge
 * said little about which one you wanted. Now there are three: the chat, what
 * the room has been doing, and how the room is set up. A tab with several
 * panels opens on the last one used, with a strip at its top to switch.
 */
export type GroupKey = 'chat' | 'tags' | 'activity' | 'settings';

export const GROUPS: Record<GroupKey, PanelKey[]> = {
  chat: ['chat'],
  // THE ROOM'S TAGS, its own tab: a tag picked there narrows everything, so
  // it is not a setting. It was also a "+ Narrow" button above each table.
  tags: ['tags'],
  // History was a third tab here, a second copy of the location bar's
  // "Recent" menu with no word on what it was. Recent has it.
  activity: ['feed', 'runs'],
  settings: ['brief', 'leads', 'shared', 'notes'],
};

export function groupOf(key: PanelKey): GroupKey {
  return (Object.keys(GROUPS) as GroupKey[]).find(g => GROUPS[g].includes(key)) ?? 'chat';
}

export interface RailTab {
  key: GroupKey;
  label: string;
  icon?: ReactNode;
  /** A dot or a count after the label: "needs you", "· on", a seat count. */
  badge?: ReactNode;
  title: string;
}

const PINNED_KEY = 'jcseo.boardroom.pinned';
// The old "chat open" switch: a reader who had folded the chat should not find
// it pinned again the first time they come back.
const OLD_CHAT_OPEN_KEY = 'jcseo.boardroom.chat.open';
const KEYS: PanelKey[] = ['feed', 'chat', 'brief', 'runs', 'tags', 'shared', 'leads', 'notes'];

function readPinned(): PanelKey | null {
  try {
    const raw = window.localStorage.getItem(PINNED_KEY);
    if (raw === 'none') return null;
    // The answer joined the brief in one tab, "State of the room" (key 'brief').
    if (raw === 'answer') return 'brief';
    // "In the room" is gone; its seats are in Recon runs.
    if (raw === 'people') return 'runs';
    // History left the rail; it is the location bar's Recent menu.
    if (raw === 'history') return 'feed';
    if (raw && (KEYS as string[]).includes(raw)) return raw as PanelKey;
    return window.localStorage.getItem(OLD_CHAT_OPEN_KEY) === '0' ? null : 'chat';
  } catch {
    return 'chat';
  }
}

/** Which panel is pinned and which is slid out, and the moves between them.
 *  Pure state; the page decides what each panel holds. */
export function usePanels(wide: boolean) {
  const [pinned, setPinned] = useState<PanelKey | null>(readPinned);
  const [overlay, setOverlay] = useState<PanelKey | null>(null);
  useEffect(() => {
    try {
      window.localStorage.setItem(PINNED_KEY, pinned ?? 'none');
    } catch {
      /* private mode: the pin is per visit */
    }
  }, [pinned]);
  // Docked only where there is room for it.
  const docked = wide ? pinned : null;
  // The panel each tab showed last, so a tab reopens where you left it.
  const last = useRef<Partial<Record<GroupKey, PanelKey>>>({});
  useEffect(() => {
    for (const k of [docked, overlay]) if (k) last.current[groupOf(k)] = k;
  }, [docked, overlay]);

  /** Bring a panel into view: it is already there if docked, else it slides out. */
  const show = useCallback(
    (key: PanelKey) => {
      if (docked === key) return;
      setOverlay(key);
    },
    [docked],
  );
  /** A rail tab: open its panel, or put a slid-out one away. */
  const toggle = useCallback(
    (key: PanelKey) => {
      if (docked === key) return;
      setOverlay(o => (o === key ? null : key));
    },
    [docked],
  );
  /** A rail tab: open its group on the panel used last, or put it away. The
   *  kept-open one is already showing. */
  const toggleGroup = useCallback(
    (group: GroupKey) => {
      if (docked && groupOf(docked) === group) return;
      setOverlay(o => (o && groupOf(o) === group ? null : last.current[group] ?? GROUPS[group][0]));
    },
    [docked],
  );
  /** The strip at a panel's top: show another panel of the same tab, in the
   *  same place (kept open stays kept open). */
  const switchTo = useCallback(
    (key: PanelKey) => {
      if (docked && groupOf(docked) === groupOf(key)) setPinned(key);
      else setOverlay(key);
    },
    [docked],
  );
  /** Pin into the slot. Whatever was pinned there is unpinned. */
  const pin = useCallback((key: PanelKey) => {
    setPinned(key);
    setOverlay(o => (o === key ? null : o));
  }, []);
  /** Out of the slot, back to sliding out while you are still reading it. */
  const unpin = useCallback(() => {
    setOverlay(pinned);
    setPinned(null);
  }, [pinned]);
  /** Put it away, wherever it is. */
  const close = useCallback(
    (key: PanelKey) => {
      if (docked === key) setPinned(null);
      setOverlay(o => (o === key ? null : o));
    },
    [docked],
  );
  return { pinned, docked, overlay, show, toggle, toggleGroup, switchTo, pin, unpin, close };
}

/** The tabs down the right edge. */
export function RightRail({
  tabs,
  docked,
  overlay,
  onPick,
}: {
  tabs: RailTab[];
  docked: PanelKey | null;
  overlay: PanelKey | null;
  onPick: (key: GroupKey) => void;
}) {
  return (
    // Scrolls when the window is short, without a scrollbar: one would take
    // the rail's whole width and cut its tabs in half.
    <nav
      className="min-h-0 overflow-y-auto flex flex-col gap-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Room panels"
      data-testid="right-rail"
    >
      {tabs.map(t => {
        const on = (!!docked && groupOf(docked) === t.key) || (!!overlay && groupOf(overlay) === t.key);
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onPick(t.key)}
            title={docked && groupOf(docked) === t.key ? `${t.title} (kept open)` : t.title}
            aria-pressed={on}
            className={`group shrink-0 flex flex-col items-center gap-1.5 py-3 px-1.5 rounded border w-8 transition-colors ${
              on
                ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-100'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            data-testid={`rail-${t.key}`}
          >
            {t.icon && <span aria-hidden className="text-xs leading-none">{t.icon}</span>}
            <span className="text-xs font-medium tracking-wide whitespace-nowrap [writing-mode:vertical-rl] rotate-180">
              {t.label}
            </span>
            {t.badge}
          </button>
        );
      })}
    </nav>
  );
}

/** Pin into the slot, or take it out. */
export function PinButton({ pinned, onClick }: { pinned: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // "KEEP OPEN", NOT "PIN". Pin also meant putting a table on the board,
      // which is "add to your list" now; one word for two things read as one.
      title={
        pinned
          ? 'Kept open beside the page. Click to let it slide away again.'
          : 'Keep it open beside the page (instead of whatever is kept open now)'
      }
      aria-pressed={pinned}
      className={`text-xs leading-none rounded border px-1.5 py-1 ${
        pinned
          ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
          : 'border-gray-300 text-gray-500 hover:text-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-100'
      }`}
      data-testid={pinned ? 'panel-unpin' : 'panel-pin'}
    >
      {pinned ? 'Kept open ✓' : 'Keep open'}
    </button>
  );
}

/** A pinned panel's frame: its title, Unpin and ×, then its body. */
export function DockedPanel({
  title,
  onUnpin,
  onClose,
  bare = false,
  children,
}: {
  title: string;
  onUnpin: () => void;
  onClose: () => void;
  /** The body draws its own header (the chat does): no title bar, just the body. */
  bare?: boolean;
  children: ReactNode;
}) {
  return (
    <aside className="min-h-0 flex flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" data-testid="docked-panel">
      {!bare && (
        <div className="shrink-0 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 px-3 py-2">
          <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">{title}</span>
          <PinButton pinned onClick={onUnpin} />
          <button type="button" onClick={onClose} title="Close" className="icon-btn text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
            ×
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 flex flex-col overflow-hidden">{children}</div>
    </aside>
  );
}

/** The strip at the top of a tab with several panels. */
export function PanelTabs({
  current,
  labels,
  onPick,
}: {
  current: PanelKey;
  labels: Partial<Record<PanelKey, ReactNode>>;
  onPick: (key: PanelKey) => void;
}) {
  const members = GROUPS[groupOf(current)];
  if (members.length < 2) return null;
  return (
    <div role="tablist" className="shrink-0 flex gap-1 border-b border-gray-200 px-2 pt-1 dark:border-gray-700" data-testid="panel-tabs">
      {members.map(k => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={k === current}
          onClick={() => onPick(k)}
          className={`-mb-px border-b-2 px-2 py-1.5 text-xs ${
            k === current
              ? 'border-indigo-500 font-medium text-indigo-700 dark:text-indigo-200'
              : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100'
          }`}
          data-testid={`panel-tab-${k}`}
        >
          {labels[k] ?? k}
        </button>
      ))}
    </div>
  );
}
